import React, { PureComponent } from 'react';
import type { Dispatch } from 'redux';

import { AudioLevelIndicator } from '../../../audio-level-indicator';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { translate } from '../../../base/i18n';
import { Icon, IconMicrophoneEmptySlash } from '../../../base/icons';
import { MEDIA_TYPE } from '../../../base/media';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantDisplayName,
    isScreenShareParticipant
} from '../../../base/participants';
import { connect } from '../../../base/redux';
import { updateSettings } from '../../../base/settings';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { isLocalTrackMuted } from '../../../base/tracks';
import { isRemoteAudioTrackMuted } from '../../../subtitles/functions.web';
import { LAYOUTS } from '../../../video-layout';
import { muteLocal } from '../../../video-menu/actions.any';
import { appendSuffix } from '../../functions';

/**
 * The type of the React {@code Component} props of {@link DisplayName}.
 */
type Props = {

  /**
   * The participant's current display name which should be shown when in
   * edit mode. Can be different from what is shown when not editing.
   */
  _configuredDisplayName: string,

  /**
   * The participant's current display name which should be shown.
   */
  _nameToDisplay: string,

  /**
   * Whether or not the display name should be editable on click.
   */
  allowEditing: boolean,

  /**
   * Invoked to update the participant's display name.
   */
  dispatch: Dispatch<any>,

  /**
   * A string to append to the displayName, if provided.
   */
  displayNameSuffix: string,

  /**
   * The ID attribute to add to the component. Useful for global querying for
   * the component by legacy components and torture tests.
   */
  elementID: string,

  /**
   * The ID of the participant whose name is being displayed.
   */
  participantID: string,

  /**
   * Whether the participant whose name is displayed is the local participant.
   * Click-to-mute is only available for the local participant.
   */
  _isLocalParticipant: boolean,

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,
};

/**
 * The type of the React {@code Component} state of {@link DisplayName}.
 */
type State = {

  /**
   * The current value of the display name in the edit field.
   */
  editDisplayNameValue: string,

  /**
   * Whether or not the component should be displaying an editable input.
   */
  isEditing: boolean,

  /**
   * The width of the container element for dynamic text sizing.
   */
  containerWidth: number,

  /**
   * The height of the container element for dynamic text sizing.
   */
  containerHeight: number,
};

/**
 * React {@code Component} for displaying and editing a participant's name.
 *
 * @augments Component
 */
class DisplayName extends PureComponent<Props, State> {
    _nameInput: ?HTMLInputElement;
    _spanRef: ?HTMLElement;
    _resizeObserver: ?ResizeObserver;

    static defaultProps = {
        _configuredDisplayName: ''
    };

    /**
   * Initializes a new {@code DisplayName} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
    constructor(props: Props) {
        super(props);

        this.state = {
            editDisplayNameValue: '',
            isEditing: false,
            containerWidth: 200
        };

        /**
     * The internal reference to the HTML element backing the React
     * {@code Component} input with id {@code editDisplayName}. It is
     * necessary for automatically selecting the display name input field
     * when starting to edit the display name.
     *
     * @private
     * @type {HTMLInputElement}
     */
        this._nameInput = null;
        this._spanRef = null;
        this._resizeObserver = null;

        this._onChange = this._onChange.bind(this);
        this._onKeyDown = this._onKeyDown.bind(this);
        this._onStartEditing = this._onStartEditing.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._setNameInputRef = this._setNameInputRef.bind(this);
        this._setSpanRef = this._setSpanRef.bind(this);
        this._onToggleAudioMute = this._onToggleAudioMute.bind(this);
    }

    /**
     * Sets up the ResizeObserver after the component mounts so that
     * containerWidth updates automatically whenever the container is resized
     * (e.g. on window resize, layout change) — not only when a Redux action
     * triggers a re-render.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this._attachResizeObserver();
    }

    /**
     * Tears down the ResizeObserver when the component unmounts to prevent
     * memory leaks.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this._detachResizeObserver();
    }

    /**
   * Automatically selects the input field's value after starting to edit the
   * display name.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidUpdate(prevProps, prevState) {
        if (!prevState.isEditing && this.state.isEditing && this._nameInput) {
            this._nameInput.select();
        }
    }

    /**
     * Attaches a ResizeObserver to the closest .videocontainer ancestor (or
     * parent element) so that containerWidth is kept in sync with the actual
     * rendered size at all times.
     *
     * @private
     * @returns {void}
     */
    _attachResizeObserver() {
        if (!this._spanRef) {
            return;
        }

        const target = this._spanRef.closest('.videocontainer') ?? this._spanRef.parentElement;

        if (!target) {
            return;
        }

        // Set the initial width immediately.
        const initialWidth = target.offsetWidth;

        if (initialWidth > 0) {
            this.setState({ containerWidth: initialWidth });
        }

        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const width = entry.contentRect.width;

                    if (width > 0) {
                        this.setState(prevState => {
                            if (prevState.containerWidth !== width) {
                                return { containerWidth: width };
                            }

                            return null;
                        });
                    }
                }
            });
            this._resizeObserver.observe(target);
        }
    }

    /**
     * Disconnects and clears the ResizeObserver.
     *
     * @private
     * @returns {void}
     */
    _detachResizeObserver() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    _setSpanRef: (HTMLElement | null) => void;

    /**
     *
     * @param {*} element
     */
    _setSpanRef(element) {
        this._spanRef = element;
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const {
            _nameToDisplay,
            allowEditing,
            displayNameSuffix,
            elementID,
            audioLevel,
            _participant,
            _filmStripView,
            _isPictureInPictureEnabled,
            _participants,
            _designation,
            _organization,
            _isFakeParticipant,
            t,
            tileViewTileLimit,
            _showAudioMutedIndicator
        } = this.props;

        if (allowEditing && this.state.isEditing) {
            return (
                <input
                    autoFocus = { true }
                    className = 'editdisplayname'
                    id = 'editDisplayName'
                    onBlur = { this._onSubmit }
                    onChange = { this._onChange }
                    onKeyDown = { this._onKeyDown }
                    placeholder = { t('defaultNickname') }
                    ref = { this._setNameInputRef }
                    spellCheck = { 'false' }
                    type = 'text'
                    value = { this.state.editDisplayNameValue } />
            );
        }
        const { containerWidth } = this.state;
        const raisedHand = _participant?.raisedHand;
        const participantLimit = tileViewTileLimit > _participants.length
            ? _participants.length
            : tileViewTileLimit;
        const showInfo = (_designation || _organization)
            && _filmStripView === LAYOUTS.TILE_VIEW
            && window.innerWidth < '500px' ? participantLimit <= 2 : participantLimit <= 8;
        const textStyle = {
            maxWidth: containerWidth - (40 + 18) - (raisedHand ? 30 : 0) - (_showAudioMutedIndicator ? showInfo ? window.innerWidth < 500 ? 25 : 0 : 25 : 46),
            display: 'block'
        };

        const participantDetails = `${_designation ?? ''}${_organization ? ` (${_organization})` : ''}`;

        const audioIndicator = _showAudioMutedIndicator
            ? (
                <Tooltip
                    className = { 'small' }
                    content = { t('videothumbnail.mute') }
                    position = { 'top' }>
                    <div className = 'audio-level-mic-icon'>
                        <Icon src = { IconMicrophoneEmptySlash } />
                    </div>
                </Tooltip>
            )
            : <AudioLevelIndicator audioLevel = { audioLevel } />;

        const infoSpan = showInfo && (_designation || _organization) && (
            <span
                className = { `displayname-info ${_isPictureInPictureEnabled ? 'displayname-pip' : 'displayname-truncate'}` }
                style = { textStyle }
                title = { participantDetails }>
                {participantDetails}
            </span>
        );

        if (_participant?.name === 'YouTube') {
            return (
                <span
                    className = { `displayname ${_isPictureInPictureEnabled ? 'displayname-pip' : 'displayname-truncate'}` }
                    id = { elementID }
                    onClick = { this._onStartEditing }
                    ref = { this._setSpanRef }
                    style = { textStyle }
                    title = { _nameToDisplay }>
                    {_nameToDisplay}
                </span>
            );
        }

        if (_isFakeParticipant) {
            return (
                <span
                    className = { `displayname ${_isPictureInPictureEnabled ? 'displayname-pip' : 'displayname-truncate'}` }
                    id = { elementID }
                    ref = { this._setSpanRef }
                    style = { textStyle }
                    title = { _nameToDisplay }>
                    {appendSuffix(_nameToDisplay, displayNameSuffix)}
                </span>
            );
        }

        if (raisedHand) {
            const raisedHandLabel = `✋ ${t('notify.raisedHand', { name: appendSuffix(_nameToDisplay, displayNameSuffix) })}`;

            return (
                <>
                    <div
                        className = 'smallvideo-display-name-raised-hand displayname'
                        id = { elementID }
                        ref = { this._setSpanRef }>
                        <span
                            className = { `${_isPictureInPictureEnabled ? 'displayname-pip' : 'displayname-truncate'}` }
                            style = { textStyle }
                            title = { raisedHandLabel }>
                            {raisedHandLabel}
                        </span>
                        {_showAudioMutedIndicator
                            ? <div className = 'audio-level-mic-icon'><Icon src = { IconMicrophoneEmptySlash } /></div>
                            : <AudioLevelIndicator audioLevel = { audioLevel } />
                        }
                    </div>
                    {infoSpan}
                </>
            );
        }

        const { _isLocalParticipant } = this.props;

        return (
            <>
                <span
                    className = { `displayname${_isLocalParticipant ? ' displayname-clickable' : ''}` }
                    id = { elementID }
                    onClick = { _isLocalParticipant ? this._onToggleAudioMute : this._onStartEditing }
                    ref = { this._setSpanRef }
                    title = { _isLocalParticipant
                        ? _showAudioMutedIndicator ? 'Click to unmute' : 'Click to mute'
                        : appendSuffix(_nameToDisplay, displayNameSuffix) }>
                    <span
                        className = { `${_isPictureInPictureEnabled ? 'displayname-pip' : 'displayname-truncate'}` }
                        style = { textStyle }>{appendSuffix(_nameToDisplay, displayNameSuffix)}</span>
                    {audioIndicator}
                </span>
                {infoSpan}
            </>
        );
    }

    _onChange: () => void;

    /**
   * Updates the internal state of the display name entered into the edit
   * field.
   *
   * @param {Object} event - DOM Event for value change.
   * @private
   * @returns {void}
   */
    _onChange(event) {
        this.setState({ editDisplayNameValue: event.target.value });
    }

    _onKeyDown: () => void;

    /**
   * Submits the editted display name update if the enter key is pressed.
   *
   * @param {Event} event - Key down event object.
   * @private
   * @returns {void}
   */
    _onKeyDown(event) {
        if (event.key === 'Enter') {
            this._onSubmit();
        }
    }

    _onStartEditing: () => void;

    /**
   * Updates the component to display an editable input field and sets the
   * initial value to the current display name.
   *
   * @private
   * @returns {void}
   */
    _onStartEditing() {
        if (this.props.allowEditing) {
            this.setState({
                isEditing: false,
                editDisplayNameValue: this.props._configuredDisplayName
            });
        }
    }

    _onToggleAudioMute: () => void;

    /**
     * Toggles the local audio mute state when the display name is clicked.
     * This only applies to the local participant; remote participant names
     * are not interactive for mute purposes.
     *
     * @param {Event} event - The click event.
     * @private
     * @returns {void}
     */
    _onToggleAudioMute(event) {
        event.stopPropagation();

        const { _isLocalParticipant, _showAudioMutedIndicator, dispatch } = this.props;

        if (!_isLocalParticipant) {
            return;
        }

        // Toggle: if currently muted → unmute (false), if not muted → mute (true)
        dispatch(muteLocal(!_showAudioMutedIndicator, MEDIA_TYPE.AUDIO));
    }

    _onSubmit: () => void;

    /**
   * Dispatches an action to update the display name if any change has
   * occurred after editing. Clears any temporary state used to keep track
   * of pending display name changes and exits editing mode.
   *
   * @param {Event} event - Key down event object.
   * @private
   * @returns {void}
   */
    _onSubmit() {
        const { editDisplayNameValue } = this.state;
        const { dispatch } = this.props;

        dispatch(updateSettings({ displayName: editDisplayNameValue }));

        this.setState({
            isEditing: false,
            editDisplayNameValue: ''
        });

        this._nameInput = null;
    }

    _setNameInputRef: (HTMLInputElement | null) => void;

    /**
   * Sets the internal reference to the HTML element backing the React
   * {@code Component} input with id {@code editDisplayName}.
   *
   * @param {HTMLInputElement} element - The DOM/HTML element for this
   * {@code Component}'s input.
   * @private
   * @returns {void}
   */
    _setNameInputRef(element) {
        this._nameInput = element;
    }
}

/**
 * Maps (parts of) the redux state to the props of this component.
 *
 * @param {Object} state - The redux store/state.
 * @param {Props} ownProps - The own props of the component.
 * @private
 * @returns {{
 *     _configuredDisplayName: string,
 *     _nameToDisplay: string
 * }}
 */
function _mapStateToProps(state, ownProps) {
    const { participantID } = ownProps;
    const participant = getParticipantById(state, participantID);
    const localParticipant = getLocalParticipant(state);
    const tracks = state['features/base/tracks'];
    const participants = state['features/base/participants'];
    const filmStripView = state['features/base/conference'].filmStripView;
    const isPictureInPictureEnabled = getPictureInPictureModeConfig(state);
    const youtubeSharedParticipantId = state['features/shared-video']?.participantSharingVideo;
    const tileViewTileLimit = state['features/filmstrip']?.tileViewTileLimit;
    const isLocalParticipant = participant?.local === true
        || (participantID && localParticipant?.id === participantID);

    let isAudioMuted = true;

    if (participant?.local) {
        isAudioMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);
    } else if (!participant?.isFakeParticipant) {
        isAudioMuted = isRemoteAudioTrackMuted(tracks, participantID);
    }

    return {
        _configuredDisplayName: participant?.name,
        _nameToDisplay: getParticipantDisplayName(state, participantID),
        _participantSharingVideo: getParticipantDisplayName(state, youtubeSharedParticipantId),
        _participant: participant,
        _showAudioMutedIndicator: isAudioMuted,
        _filmStripView: filmStripView,
        _designation: participant?.designation,
        _organization: participant?.organization,
        tileViewTileLimit,
        localParticipant: participant?.local,
        _isLocalParticipant: isLocalParticipant,
        _isFakeParticipant: isScreenShareParticipant(getParticipantById(state, participantID)),
        _participants: participants,
        _isPictureInPictureEnabled: isPictureInPictureEnabled
    };
}

export default translate(connect(_mapStateToProps)(DisplayName));
